import { Camera as CapacitorCamera } from '@capacitor/camera'
import { CameraPreview } from '@capacitor-community/camera-preview'
import { CameraPreviewOptions } from '@capacitor-community/camera-preview/dist/esm/definitions'
import { IonFooter, IonHeader, IonPage } from '@ionic/react'
import { ParseKeys } from 'i18next'
import { camera, cameraReverse, close } from 'ionicons/icons'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Prompt } from 'react-router-dom'
import { isMobileApp } from '~/shared/config/constants'
import { Button, IconButton } from '~/shared/ui/Buttons'
import { BODY_MAX_WIDTH, myPalette } from '~/shared/ui/theme'
import { CameraContext } from './context'
import { Painter } from './Painter'
import {
  IonToolbar,
  CameraAltButton,
  ToolbarWrapper,
  ErrorMessage,
  HelperText,
  CameraPreviewContent,
} from './styled'

type CameraProviderProps = {
  children: ReactNode
}

export function CameraProvider({ children }: CameraProviderProps) {
  const { t } = useTranslation()

  const [isPermissionError, togglePermissionError] = useState(false)
  const [cameraPosition, setCameraPosition] = useState<'rear' | 'front'>('rear')

  const [isCameraOpen, toggleIsCameraOpen] = useState(false)
  const [imageFile, setImageFile] = useState<File>()
  const [helperIndex, setHelperIndex] = useState<number>(0)
  const [cameraParams, setCameraParams] = useState<{
    fn: (file: File) => void
    canPaint?: boolean
    helpers?: ParseKeys[]
  }>()

  const cameraOptions: CameraPreviewOptions = useMemo(
    () => ({
      parent: 'cameraPreview',
      position: cameraPosition,
      toBack: true,
      disableAudio: true,
      enableHighResolution: true,
      enableOpacity: true,
      ...(isMobileApp
        ? {
            enableZoom: true,
            lockAndroidOrientation: true,
            rotateWhenOrientationChanged: false,
          }
        : {
            height: 1920,
          }),
    }),
    [cameraPosition],
  )

  const handleCameraStart = useCallback(async () => {
    const permission = await CapacitorCamera.checkPermissions()

    if (permission.camera === 'denied') {
      togglePermissionError(true)
      return
    }

    await CameraPreview.start(cameraOptions)
  }, [cameraOptions])

  const handleCameraOpen = useCallback(
    async (
      fn: (file: File) => void,
      canPaint?: boolean,
      helpers?: ParseKeys[],
    ) => {
      setCameraParams({ fn, canPaint, helpers })
      toggleIsCameraOpen(true)
      handleCameraStart()
    },
    [handleCameraStart],
  )

  const handleCameraClose = useCallback(async () => {
    try {
      await CameraPreview.stop()
    } finally {
      toggleIsCameraOpen(false)
      setCameraParams(undefined)
      setHelperIndex(0)
      setImageFile(undefined)
    }
  }, [])

  const handleFileDelete = () => {
    setHelperIndex((prevIndex) => prevIndex - 1)
    setImageFile(undefined)
  }

  const handleImageSave = async (file: File) => {
    setImageFile(undefined)
    cameraParams?.fn?.(file)
  }

  const handleCameraFlip = async () => {
    if (isMobileApp) {
      await CameraPreview.flip()
    } else {
      const position = cameraPosition === 'rear' ? 'front' : 'rear'
      setCameraPosition(position)

      await CameraPreview.stop()
      await CameraPreview.start({ ...cameraOptions, position })
    }
  }

  const handleCameraCapture = async () => {
    const res = await CameraPreview.capture({
      quality: 100,
    })
    const fileString = `data:image/png;base64,${res.value}`

    const response = await fetch(fileString as string).then((res) => res.blob())
    const resFile = new File([response as Blob], 'Photo', {
      type: 'image/png',
    })
    setImageFile(resFile)
    setHelperIndex((prevIndex) => prevIndex + 1)
  }

  const helperText = useMemo(() => {
    const helper = cameraParams?.helpers?.[helperIndex]

    return helper ? helper : ''
  }, [cameraParams?.helpers, helperIndex])

  return (
    <>
      {isCameraOpen && (
        <IonPage
          style={{ margin: '0 auto', maxWidth: BODY_MAX_WIDTH, width: '100%' }}
        >
          <Prompt
            when={isCameraOpen}
            message={() => {
              handleCameraClose()
              return false
            }}
          />
          <IonHeader style={{ backgroundColor: myPalette.dark.shadowLight }}>
            <IonToolbar>
              <IconButton
                onClick={handleCameraClose}
                icon={close}
                style={{ fontSize: '22px' }}
                iconProps={{
                  style: { color: myPalette.aliceBlue.main },
                }}
                data-testid='camera-close'
              />
            </IonToolbar>
          </IonHeader>

          {imageFile && (
            <Painter
              canPaint={cameraParams?.canPaint}
              imageFile={imageFile}
              onDelete={handleFileDelete}
              onOk={handleImageSave}
            />
          )}

          <CameraPreviewContent id='cameraPreview' />

          {isPermissionError && (
            <ErrorMessage>
              {t(
                'alert.allow camera access and restart the app to be able to take and attach photos to the vehicle inspection',
              )}
              <Button onClick={handleCameraClose} variant='crimsonOutline'>
                {t('button.close')}
              </Button>
            </ErrorMessage>
          )}

          {!isPermissionError && !imageFile && helperText && (
            <HelperText data-testid={`helper-text-${helperText}`}>
              {t(helperText)}
            </HelperText>
          )}

          {!imageFile && !isPermissionError && (
            <IonFooter>
              <IonToolbar>
                <ToolbarWrapper>
                  <IconButton
                    onClick={handleCameraFlip}
                    icon={cameraReverse}
                    style={{ fontSize: '30px' }}
                    iconProps={{
                      style: {
                        color: myPalette.aliceBlue.main,
                      },
                    }}
                  />

                  <CameraAltButton
                    onClick={handleCameraCapture}
                    icon={camera}
                    iconProps={{ style: { color: myPalette.aliceBlue.main } }}
                    data-testid='camera-capture'
                  />
                </ToolbarWrapper>
              </IonToolbar>
            </IonFooter>
          )}
        </IonPage>
      )}

      <CameraContext.Provider
        value={{
          isCameraOpen,
          cameraOpen: handleCameraOpen,
          cameraClose: handleCameraClose,
        }}
      >
        <div hidden={isCameraOpen}>{children}</div>
      </CameraContext.Provider>
    </>
  )
}
